import { useMemo } from 'react';
import { useField } from 'formik';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Card,
  Grid,
  Button,
  IconButton,
  Typography,
  FormHelperText,
} from '@mui/material';

import { questionLabelText } from 'utils/question-label';
import { FormFieldType, FormFieldTypeTitle } from 'types';
import { InputField, RadioField, SwitchField, RichTextField } from 'components';
import {
  typeOptions,
  getDefaultField,
  typeOptionsWithoutFieldArray,
} from 'utils/form';
import {
  labelHiddenFields,
  requiredHiddenFields,
  placeholderHiddenFields,
} from 'utils/validation';

import { FormInputField } from './input';
import { FormRadioField } from './radio';
import { FormFieldProps } from './types';
import { FormSelectField } from './select';
import { FormCheckboxField } from './checkbox';
import { getNonScalingTransform } from './dnd';
import { FormOutcomesField } from './outcomes';
import { FormCsvLookupField } from './csv-lookup';
import { FormFieldArrayField } from './field-array';
import { FormScaleRatingField } from './scale-rating';
import { EMPTY_OUTCOME_FIELD_SOURCES } from './config';
import { CopyPasteActions } from './copy-paste-actions';
import { FormOutcomeFieldsField } from './outcome-fields';

export const FieldView = ({
  name,
  value,
  onCopy,
  onRemove,
  canDelete,
  sortableId,
  onPasteAfter,
  containerPath,
  isDragging = false,
  simplified = false,
  allowInputId = false,
  allowOutcomes = false,
  allowPageBreak = true,
  allowFieldArray = true,
  allowOutcomeFields = false,
  allowReferenceCodes = false,
  outcomeFieldSources = EMPTY_OUTCOME_FIELD_SOURCES,
}: FormFieldProps) => {
  const [, { error }, { setValue }] = useField(name);
  const { id, type } = value;
  const sortable = useSortable({
    id: sortableId ?? id,
    disabled: !sortableId,
    data: containerPath ? { containerId: containerPath } : undefined,
  });

  const isFieldArray = type === FormFieldType.FIELD_ARRAY;
  const isPageBreak = type === FormFieldType.PAGE_BREAK;
  const isHeading = type === FormFieldType.HEADING;
  const isText = type === FormFieldType.TEXT;
  const canUseQuestionCode =
    allowReferenceCodes &&
    !isPageBreak &&
    !isHeading &&
    !isText &&
    !isFieldArray;
  const isLabelHidden = labelHiddenFields.has(type);
  const isPlaceholderHidden = placeholderHiddenFields.has(type);
  const trimmedLabel = questionLabelText(value.label).trim();
  const fieldSubtitle =
    trimmedLabel ||
    (type === FormFieldType.FIELD_ARRAY ? undefined : 'Untitled field');
  const typeTitle = FormFieldTypeTitle[type];
  const availableTypeOptions = useMemo(() => {
    let options = allowFieldArray ? typeOptions : typeOptionsWithoutFieldArray;

    if (!allowOutcomes) {
      options = options.filter((item) => item.value !== FormFieldType.OUTCOMES);
    }

    if (!allowOutcomeFields) {
      options = options.filter(
        (item) => item.value !== FormFieldType.OUTCOME_FIELDS
      );
    }

    if (!allowPageBreak) {
      options = options.filter(
        (item) => item.value !== FormFieldType.PAGE_BREAK
      );
    }

    return options;
  }, [allowFieldArray, allowOutcomes, allowOutcomeFields, allowPageBreak]);

  const dragTransform = getNonScalingTransform(sortable.transform);
  const canInteractWithField = !simplified;

  return (
    <Card
      className="p-4"
      variant="outlined"
      ref={sortableId ? sortable.setNodeRef : undefined}
      style={
        sortableId
          ? {
              ...(isDragging && { zIndex: 20, position: 'relative' }),
              opacity: isDragging ? 0.45 : 1,
              transition: sortable.transition,
              transform: CSS.Transform.toString(dragTransform),
            }
          : undefined
      }
    >
      <div className="flex flex-col gap-4">
        {simplified && (
          <div className="flex min-w-0 items-center gap-3">
            {sortableId && (
              <IconButton
                size="small"
                aria-label="Drag field"
                className="cursor-grab border border-current/20"
                {...sortable.attributes}
                {...sortable.listeners}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            )}

            <div className="min-w-0">
              <Typography variant="subtitle2">{typeTitle}</Typography>
              {fieldSubtitle && (
                <Typography variant="caption" color="textSecondary">
                  {fieldSubtitle}
                </Typography>
              )}
            </div>
          </div>
        )}

        {canInteractWithField && (
          <>
            <div className="px-1 py-1">
              <RadioField
                row
                label="Type"
                name={`${name}.type`}
                options={availableTypeOptions}
                onChange={(event) => {
                  const newType = event.target.value as FormFieldType;
                  setValue({
                    ...getDefaultField(newType),
                    id,
                    type: newType,
                  });
                }}
              />
            </div>

            {!isText &&
              !(isPlaceholderHidden && isLabelHidden && isPageBreak) && (
                <Grid container spacing={2}>
                  {!isLabelHidden && (
                    <Grid size={{ xs: 12, md: isHeading ? 6 : 12 }}>
                      {isHeading ? (
                        <InputField
                          fullWidth
                          label="Title"
                          placeholder="Title"
                          name={`${name}.label`}
                        />
                      ) : (
                        <RichTextField
                          label="Label"
                          acceptPlainText
                          allowImages={false}
                          name={`${name}.label`}
                          placeholder="Enter a question label"
                        />
                      )}
                    </Grid>
                  )}

                  {isHeading && (
                    <Grid size={{ md: 6, xs: 12 }}>
                      <InputField
                        fullWidth
                        placeholder="Subtitle"
                        name={`${name}.subtitle`}
                        label="Subtitle (optional)"
                      />
                    </Grid>
                  )}

                  {!isPlaceholderHidden && (
                    <Grid size={{ md: 6, xs: 12 }}>
                      <InputField
                        fullWidth
                        label="Placeholder"
                        placeholder="Placeholder"
                        name={`${name}.placeholder`}
                      />
                    </Grid>
                  )}

                  {!isPageBreak &&
                    !isHeading &&
                    allowInputId &&
                    type !== FormFieldType.OUTCOMES &&
                    type !== FormFieldType.OUTCOME_FIELDS && (
                      <Grid size={{ md: 6, xs: 12 }}>
                        <InputField
                          fullWidth
                          placeholder="Input ID"
                          name={`${name}.inputId`}
                          label="Input ID (optional)"
                        />
                      </Grid>
                    )}

                  {canUseQuestionCode && (
                    <Grid size={{ md: 6, xs: 12 }}>
                      <InputField
                        fullWidth
                        label="Question code"
                        placeholder="Question code"
                        name={`${name}.referenceCode`}
                      />
                    </Grid>
                  )}
                </Grid>
              )}

            {isText && (
              <RichTextField
                label="Text"
                allowImages={false}
                name={`${name}.text`}
              />
            )}

            {type === FormFieldType.INPUT && (
              <FormInputField name={name} value={value} />
            )}

            {type === FormFieldType.SELECT && (
              <FormSelectField
                name={name}
                value={value}
                allowReferenceCodes={allowReferenceCodes}
              />
            )}

            {type === FormFieldType.RADIO && (
              <FormRadioField
                name={name}
                value={value}
                allowReferenceCodes={allowReferenceCodes}
              />
            )}

            {type === FormFieldType.CHECKBOX && (
              <FormCheckboxField
                name={name}
                value={value}
                allowReferenceCodes={allowReferenceCodes}
              />
            )}

            {type === FormFieldType.SCALE_RATING && (
              <FormScaleRatingField
                name={name}
                value={value}
                allowReferenceCodes={allowReferenceCodes}
              />
            )}

            {type === FormFieldType.OUTCOMES && (
              <FormOutcomesField name={name} value={value} />
            )}

            {type === FormFieldType.CSV_LOOKUP && (
              <FormCsvLookupField name={name} value={value} />
            )}
          </>
        )}

        {type === FormFieldType.OUTCOME_FIELDS && (
          <FormOutcomeFieldsField
            name={name}
            value={value}
            simplified={simplified}
            allowReferenceCodes={allowReferenceCodes}
            outcomeFieldSources={outcomeFieldSources}
          />
        )}

        {isFieldArray && (
          <FormFieldArrayField
            name={name}
            value={value}
            simplified={simplified}
            allowInputId={allowInputId}
            allowOutcomes={allowOutcomes}
            allowOutcomeFields={allowOutcomeFields}
            allowReferenceCodes={allowReferenceCodes}
          />
        )}

        {isPageBreak && error && typeof error === 'string' && (
          <FormHelperText error>{error}</FormHelperText>
        )}
      </div>

      {canInteractWithField && (
        <div className="mt-4 flex items-center justify-between gap-2 border-t-1 border-gray-300 pt-2">
          <div className="flex items-center gap-2">
            {!isFieldArray && !requiredHiddenFields.has(type) && (
              <SwitchField label="Required" name={`${name}.required`} />
            )}

            <CopyPasteActions
              onCopy={onCopy}
              entityLabel="field"
              onPasteAfter={onPasteAfter}
            />
          </div>

          <div>
            <Button
              color="error"
              onClick={onRemove}
              disabled={!canDelete}
              startIcon={<DeleteIcon />}
            >
              Delete
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};
